export interface FormatDateOptions {
  locale?: string | string[];
  formatOptions?: Intl.DateTimeFormatOptions;
  inputUnit?: 'auto' | 'seconds' | 'milliseconds';
  invalidValue?: string;
}

interface BinarySizeFormatOptions {
  decimals: number;
  units: readonly string[];
  zeroValue: string;
  invalidValue: string;
  stripTrailingZeros: boolean;
}

const BINARY_BASE = 1024;
const SECOND_TIMESTAMP_THRESHOLD = 10_000_000_000;

function formatBinarySize(bytes: number, options: BinarySizeFormatOptions): string {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return options.invalidValue;
  }

  if (bytes === 0) {
    return options.zeroValue;
  }

  const unitIndex = Math.min(
    Math.floor(Math.log(bytes) / Math.log(BINARY_BASE)),
    options.units.length - 1
  );
  const value = bytes / BINARY_BASE ** unitIndex;
  const formattedValue = value.toFixed(Math.max(0, options.decimals));
  const normalizedValue = options.stripTrailingZeros
    ? String(parseFloat(formattedValue))
    : formattedValue;

  return `${normalizedValue} ${options.units[unitIndex]}`;
}

function toDate(value: string | number | Date, inputUnit: FormatDateOptions['inputUnit']): Date {
  if (value instanceof Date) {
    return value;
  }

  if (typeof value === 'number') {
    const isSeconds =
      inputUnit === 'seconds' ||
      (inputUnit !== 'milliseconds' && Math.abs(value) < SECOND_TIMESTAMP_THRESHOLD);

    return new Date(isSeconds ? value * 1000 : value);
  }

  return new Date(value);
}

export function formatBytes(bytes: number, decimals = 2): string {
  return formatBinarySize(bytes, {
    decimals,
    units: ['Bytes', 'KB', 'MB', 'GB', 'TB'],
    zeroValue: '0 Bytes',
    invalidValue: '0 Bytes',
    stripTrailingZeros: true,
  });
}

export function formatFileSize(bytes: number, decimals = 2): string {
  return formatBinarySize(bytes, {
    decimals,
    units: ['B', 'KB', 'MB', 'GB', 'TB'],
    zeroValue: '0 B',
    invalidValue: '0 B',
    stripTrailingZeros: false,
  });
}

export function formatDate(
  value: string | number | Date,
  {
    locale = 'ja-JP',
    formatOptions = {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    },
    inputUnit = 'auto',
    invalidValue = 'Invalid date',
  }: FormatDateOptions = {}
): string {
  const date = toDate(value, inputUnit);

  if (Number.isNaN(date.getTime())) {
    return invalidValue;
  }

  return new Intl.DateTimeFormat(locale, formatOptions).format(date);
}

export function calculateStoragePercentage(used: number, limit: number): number {
  if (!Number.isFinite(used) || !Number.isFinite(limit) || used < 0 || limit <= 0) {
    return 0;
  }

  return Math.min(Math.round((used / limit) * 100), 100);
}
